import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { PageShell } from '../components/PageShell';
import { KindIcon } from '../components/KindIcon';
import { colors, cardStyle } from '../theme';
import { useIdentity } from '../useIdentity';
import { sanitizeName } from '../nameUtils';
import { inputStyle, labelStyle } from '../formPrimitives';
import { deployComposeApp, type ComposeDeployResult } from '../api';

const SAMPLE = `services:
  web:
    image: nginx:alpine
    ports:
      - "80"
`;

type State =
  | { phase: 'idle' }
  | { phase: 'deploying' }
  | { phase: 'done'; result: ComposeDeployResult }
  | { phase: 'error'; message: string };

/** "Compose App": paste a docker-compose file, get a URL. */
export function ComposeAppPage() {
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const { email } = useIdentity();
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('development');
  const [composeYaml, setComposeYaml] = useState(SAMPLE);
  const [state, setState] = useState<State>({ phase: 'idle' });

  const canSubmit = name.length > 0 && composeYaml.trim().length > 0 && state.phase !== 'deploying';

  const submit = async () => {
    setState({ phase: 'deploying' });
    try {
      const result = await deployComposeApp(identityApi, email, { name, composeYaml, environment });
      setState({ phase: 'done', result });
    } catch (e) {
      setState({ phase: 'error', message: (e as Error).message });
    }
  };

  return (
    <PageShell>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 32 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24 }}>
          <KindIcon kind="compose" />
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: colors.text }}>Compose App</h1>
            <div style={{ color: colors.textMuted, fontSize: 14 }}>
              Paste a docker-compose file. Your first service with a port gets an HTTPS URL.
            </div>
          </div>
        </div>
        <div style={{ ...cardStyle, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label style={labelStyle}>
            Application name
            <input
              style={inputStyle}
              value={name}
              placeholder="my-stack"
              onChange={(e) => setName(sanitizeName(e.target.value))}
            />
          </label>
          <label style={labelStyle}>
            Environment
            <select style={inputStyle} value={environment} onChange={(e) => setEnvironment(e.target.value)}>
              <option value="development">Development</option>
              <option value="staging">Staging</option>
            </select>
          </label>
          <label style={labelStyle}>
            docker-compose.yml
            <textarea
              style={{ ...inputStyle, fontFamily: 'monospace', minHeight: 260, resize: 'vertical' }}
              value={composeYaml}
              spellCheck={false}
              onChange={(e) => setComposeYaml(e.target.value)}
            />
          </label>
          <div style={{ fontSize: 12, color: colors.textMuted }}>
            Use <code>image:</code> (no <code>build:</code>). Host-path mounts, privileged containers and host
            networking aren't allowed — use named volumes.
          </div>
          {state.phase === 'error' && (
            <div style={{ color: '#b91c1c', fontSize: 14 }} role="alert">
              {state.message}
            </div>
          )}
          {state.phase === 'done' ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ color: colors.text }}>Deploying — it may take up to a minute for HTTPS to be ready:</span>
              <a href={state.result.url} target="_blank" rel="noreferrer">
                {state.result.url}
              </a>
              <button type="button" onClick={() => navigate('/applications')} style={{ marginLeft: 'auto' }}>
                My Applications
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              style={{
                alignSelf: 'flex-start',
                background: canSubmit ? colors.primary : colors.border,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {state.phase === 'deploying' ? 'Deploying…' : 'Deploy'}
            </button>
          )}
        </div>
      </div>
    </PageShell>
  );
}
